import { usePage, useForm, router } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import DashboardLayout from '../../../../components/DashboardLayout';
import TipTapEditor from '@/components/editor/TipTapEditor';
import PostPickerModal from '@/components/admin/newsletter/PostPickerModal';
import ScheduleModal from '@/components/admin/newsletter/ScheduleModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface Newsletter {
  id: number;
  name: string;
  slug: string;
}

interface Issue {
  id: number;
  subject: string;
  preheader: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  issue_number: number | null;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  ready_to_send: boolean;
  editable: boolean;
  recipient_count: number;
  tracked_posts_count: number;
  content: {
    id?: number;
    html_content: string;
    text_content: string;
    ai_markdown?: string;
    tiptap_json?: Record<string, unknown>;
  } | null;
  ai_markdown?: string;
  tiptap_json?: Record<string, unknown>;
}

interface Props {
  newsletter: Newsletter;
  issue: Issue;
  errors?: string[];
  [key: string]: any;
}

export default function Edit() {
  const { newsletter, issue, errors: initialErrors = [] } = usePage<Props>().props;
  
  const [showPostPicker, setShowPostPicker] = useState(false);
  const [htmlContent, setHtmlContent] = useState(issue?.content?.html_content || '');
  const [textContent, setTextContent] = useState(issue?.content?.text_content || '');
  const [aiMarkdown, setAiMarkdown] = useState(issue?.content?.ai_markdown || issue?.ai_markdown || '');
  const [tiptapJson, setTiptapJson] = useState<Record<string, unknown>>(issue?.content?.tiptap_json || issue?.tiptap_json || {});
  const [showPreview, setShowPreview] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTestEmail, setShowTestEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  const { data, setData, put, errors, processing } = useForm({
    subject: issue?.subject || '',
    preheader: issue?.preheader || '',
    content_attributes: {
      id: issue?.content?.id,
      html_content: htmlContent,
      text_content: textContent,
    },
    ai_markdown: aiMarkdown,
    tiptap_json: tiptapJson,
  });

  // Update content when editor changes
  useEffect(() => {
    setData('content_attributes', {
      id: issue?.content?.id,
      html_content: htmlContent,
      text_content: textContent,
    });
  }, [htmlContent, textContent]);

  // Update AI markdown when it changes
  useEffect(() => {
    setData('ai_markdown', aiMarkdown);
  }, [aiMarkdown]);

  // Update tiptap_json when editor changes
  useEffect(() => {
    setData('tiptap_json', tiptapJson);
  }, [tiptapJson]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    put(`/admin/newsletters/${newsletter.id}/issues/${issue.id}`, {
      preserveScroll: true,
    });
  };

  const handleEditorChange = (html: string, json: Record<string, unknown>) => {
    setHtmlContent(html);
    setTiptapJson(json);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    setTextContent(plainText);
  };

  const handleAiMarkdownChange = (markdown: string) => {
    setAiMarkdown(markdown);
  };

  const handleInsertPost = (post: { title: string; slug: string }) => {
    const linkHtml = `<a href="/blog/${post.slug}">${post.title}</a>`;
    setHtmlContent((prev) => prev + linkHtml);
    setShowPostPicker(false);
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleSendNow = () => {
    if (confirm('Are you sure you want to send this newsletter now?')) {
      router.post(`/admin/newsletters/${newsletter.id}/issues/${issue.id}/send_now`);
    }
  };

  const handleSendTest = (e: React.FormEvent) => {
    e.preventDefault();
    router.post(`/admin/newsletters/${newsletter.id}/issues/${issue.id}/send_test`, {
      newsletter_issue: { test_email: testEmail },
    }, {
      onSuccess: () => {
        setShowTestEmail(false);
        setTestEmail('');
        alert('Test email sent! Check your Rails logs.');
      },
    });
  };

  const canEdit = issue.editable;
  const canSend = issue.ready_to_send && issue.recipient_count > 0 && (issue.status === 'draft' || issue.status === 'scheduled');
  const isScheduled = issue.status === 'scheduled';

  return (
    <DashboardLayout>
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <a
          href={`/admin/newsletters/${newsletter.id}/issues`}
          className="text-primary hover:text-primary/80"
        >
          ← Back to Issues
        </a>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          Edit Issue #{issue.issue_number} - {newsletter.name}
        </h1>
        
        <div className="flex gap-2">
          {canSend && (
            <button
              onClick={handleSendNow}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Send Now
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => setShowSchedule(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isScheduled ? 'Reschedule' : 'Schedule'}
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      {issue.status !== 'draft' && (
        <div className="mb-6 p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            This issue is <strong>{issue.status}</strong>
            {issue.scheduled_at && ` - Scheduled for ${new Date(issue.scheduled_at).toLocaleString()}`}
            {issue.sent_at && ` - Sent at ${new Date(issue.sent_at).toLocaleString()}`}
          </p>
        </div>
      )}

      {initialErrors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <ul className="list-disc list-inside text-red-600 dark:text-red-400">
            {initialErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {canEdit ? (
        <form onSubmit={handleSubmit}>
          {/* Subject */}
          <div className="mb-6">
            <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
              Subject Line *
            </label>
            <input
              type="text"
              id="subject"
              value={data.subject}
              onChange={(e) => setData('subject', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-primary focus:border-primary bg-background text-foreground"
              required
            />
          </div>

          {/* Preheader */}
          <div className="mb-6">
            <label htmlFor="preheader" className="block text-sm font-medium text-foreground mb-2">
              Preview Text
            </label>
            <input
              type="text"
              id="preheader"
              value={data.preheader}
              onChange={(e) => setData('preheader', e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg focus:ring-primary focus:border-primary bg-background text-foreground"
              maxLength={150}
            />
          </div>

          {/* Content Editor */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-foreground">
                Newsletter Content
              </label>
              <button
                type="button"
                onClick={() => setShowPostPicker(true)}
                className="px-3 py-1 text-sm bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
              >
                + Insert Blog Post
              </button>
            </div>
            
            <div className="border border-border rounded-lg overflow-hidden">
              <TipTapEditor
                content={htmlContent}
                tiptapJson={tiptapJson}
                onChange={handleEditorChange}
                editable={true}
                enableAiMarkdown={true}
                aiMarkdown={aiMarkdown}
                onAiMarkdownChange={handleAiMarkdownChange}
                showConvertButton={true}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => setShowTestEmail(true)}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
            >
              Send Test Email
            </button>
            
            <button
              type="button"
              onClick={handlePreview}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50"
              disabled={!htmlContent}
            >
              Preview
            </button>
            
            <button
              type="submit"
              disabled={processing}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {processing ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      ) : (
        // View mode (when not draft)
        <div>
          <div className="mb-6">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">Subject</h3>
            <p className="text-lg text-foreground">{issue.subject}</p>
          </div>
          
          {issue.content && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">Content</h3>
              <div 
                className="prose max-w-none p-4 border border-border rounded-lg bg-muted"
                dangerouslySetInnerHTML={{ __html: issue.content.html_content }}
              />
            </div>
          )}
        </div>
      )}

      {/* Post Picker Modal */}
      {showPostPicker && (
        <PostPickerModal
          onClose={() => setShowPostPicker(false)}
          onSelect={handleInsertPost}
        />
      )}

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview - {issue.subject}</DialogTitle>
            <DialogDescription>
              This is how your newsletter will appear to recipients.
            </DialogDescription>
          </DialogHeader>
          <div 
            className="prose dark:prose-invert max-w-none p-4 border border-border rounded-lg bg-muted"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </DialogContent>
      </Dialog>

      {/* Schedule Modal */}
      <ScheduleModal
        open={showSchedule}
        onOpenChange={setShowSchedule}
        newsletterId={newsletter.id}
        issueId={issue.id}
        status={issue.status}
        scheduledAt={issue.scheduled_at}
        editable={issue.editable}
      />

      {/* Test Email Modal */}
      <Dialog open={showTestEmail} onOpenChange={setShowTestEmail}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Enter an email address to receive a test version of this newsletter.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSendTest}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <label htmlFor="test-email" className="text-sm font-medium">Email Address</label>
                <input
                  id="test-email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              <p className="text-sm text-muted-foreground">
                The test email will be logged to your Rails console.
              </p>
            </div>
            <DialogFooter>
              <button
                type="submit"
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              >
                Send Test
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
